'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import TourHeader from '@/components/tour/TourHeader';
import MatchCard from '@/components/tour/MatchCard';
import PointsTable from '@/components/tour/PointsTable';
import SquadsTab from '@/components/tour/SquadsTab';
import StatsTab from '@/components/tour/StatsTab';
import type { Tour } from '@/lib/tourData';


type TabType = 'matches' | 'videos' | 'points-table' | 'squads' | 'stats';
type MatchFilter = 'live-upcoming' | 'completed';

interface TourPageClientProps {
  tourId: string;
  initialData: Tour;
}

function VideoCard({ title, duration, thumbnail }: {title: string;duration: string;thumbnail: string;}) {
  return (
    <div className="group cursor-pointer">
      <div className="relative rounded-lg overflow-hidden aspect-video" style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
        <img src={thumbnail} alt={`${title} cricket video highlight thumbnail`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-rc-orange/90 flex items-center justify-center group-hover:scale-110 transition-transform">
            <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
          {duration}
        </div>
      </div>
      <p className="text-white/85 text-sm font-medium mt-2 line-clamp-2">{title}</p>
    </div>);

}

const videoHighlights = [
{ title: 'Mumbai City Aces vs Delhi Dynamos Royals - Match 1 Highlights', duration: '8:42', thumbnail: "https://images.unsplash.com/photo-1672570212985-d4f6437aa822" },
{ title: 'Rohit Sharma 87 off 52 balls - Stunning Innings | Match 1', duration: '5:18', thumbnail: "https://images.unsplash.com/photo-1640786172860-a24d7a007976" },
{ title: 'Andre Russell 95 off 42 balls - Explosive Batting | Match 3', duration: '6:05', thumbnail: "https://images.unsplash.com/photo-1631556551464-5864265ae536" },
{ title: 'Jasprit Bumrah 3 wickets - Unplayable Spell | Match 1', duration: '4:33', thumbnail: "https://images.unsplash.com/photo-1655405187490-030797137cb9" },
{ title: 'Chennai Storm Kings vs Bangalore Blaze - Match 2 Highlights', duration: '9:15', thumbnail: "https://images.unsplash.com/photo-1637940671686-22d319326c29" },
{ title: 'MS Dhoni Finishes in Style - 55* off 28 | Match 2', duration: '3:47', thumbnail: "https://images.unsplash.com/photo-1687002805428-af97ba3a3423" }];


export default function TourPageClient({ tourId, initialData }: TourPageClientProps) {
  const [tour, setTour] = useState<Tour>(initialData);
  const [activeTab, setActiveTab] = useState<TabType>('matches');
  const [matchFilter, setMatchFilter] = useState<MatchFilter>('live-upcoming');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/tours/${tourId}`);
        if (res.ok) {
          const data = await res.json();
          if (data) setTour(data);
        }
      } catch {

        // use initial data
      } finally {setLoading(false);
      }
    };
    fetchTour();
  }, [tourId]);

  const liveAndUpcomingMatches = tour.matches?.filter(
    (m) => m.status === 'live' || m.status === 'upcoming'
  ) || [];

  const completedMatches = tour.matches?.filter(
    (m) => m.status === 'completed'
  ) || [];

  const todayMatches = liveAndUpcomingMatches.filter(
    (m) => m.date === 'Today' || m.status === 'live'
  );
  const upcomingMatches = liveAndUpcomingMatches.filter(
    (m) => m.date !== 'Today' && m.status !== 'live'
  );

  const tabs: {id: TabType;label: string;}[] = [
  { id: 'matches', label: 'Matches' },
  { id: 'videos', label: 'Videos' },
  { id: 'points-table', label: 'Points Table' },
  { id: 'squads', label: 'Squads' },
  { id: 'stats', label: 'Stats' }];


  return (
    <div className="min-h-screen bg-rc-bg">
      <Navbar />
      <main className="pt-14">
        {/* Tour Header */}
        <TourHeader tour={tour} />

        {/* Tab Navigation */}
        <div
          className="sticky top-14 z-40 border-b border-rc-border"
          style={{ backgroundColor: '#07090d' }}>
          
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) =>
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-shrink-0 px-5 py-4 text-sm font-semibold transition-all duration-200 ${
                activeTab === tab.id ?
                'text-rc-orange' : 'text-white/50 hover:text-white/80'}`
                }>
                
                  {tab.label}
                  {activeTab === tab.id &&
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-rc-orange" />
                }
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main Content */}
            <div className="flex-1 min-w-0">

              {/* MATCHES TAB */}
              {activeTab === 'matches' &&
              <div>
                  {/* Filter buttons */}
                  <div className="flex gap-2 mb-6">
                    <button
                    onClick={() => setMatchFilter('live-upcoming')}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    matchFilter === 'live-upcoming' ? 'bg-rc-orange text-white' : 'bg-rc-card border border-rc-border text-white/60 hover:text-white'}`
                    }>
                    
                      Live &amp; Upcoming
                    </button>
                    <button
                    onClick={() => setMatchFilter('completed')}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    matchFilter === 'completed' ?
                    'bg-rc-orange text-white' : 'bg-rc-card border border-rc-border text-white/60 hover:text-white'}`
                    }>
                    
                      Completed
                    </button>
                  </div>

                  {matchFilter === 'live-upcoming' &&
                <div className="space-y-6">
                      {/* Today section */}
                      {todayMatches.length > 0 &&
                  <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-0.5 h-5 bg-rc-orange rounded-full" />
                            <h2 className="text-white text-lg font-bold">Today</h2>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {todayMatches.map((match) =>
                      <MatchCard key={match.id} match={match} tourId={tourId} />
                      )}
                          </div>
                        </div>
                  }

                      {/* Upcoming section */}
                      {upcomingMatches.length > 0 &&
                  <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-0.5 h-5 bg-white/20 rounded-full" />
                            <h2 className="text-white text-lg font-bold">Upcoming</h2>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {upcomingMatches.map((match) =>
                      <MatchCard key={match.id} match={match} tourId={tourId} />
                      )}
                          </div>
                        </div>
                  }

                      {liveAndUpcomingMatches.length === 0 &&
                  <div className="text-center py-16">
                          <div className="w-16 h-16 rounded-full bg-rc-card border border-rc-border flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-white/40 text-sm">No live or upcoming matches</p>
                        </div>
                  }
                    </div>
                }

                  {matchFilter === 'completed' &&
                <div>
                      {completedMatches.length > 0 ?
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {completedMatches.map((match) =>
                    <MatchCard key={match.id} match={match} tourId={tourId} />
                    )}
                        </div> :

                  <div className="text-center py-16">
                          <p className="text-white/40 text-sm">No completed matches yet</p>
                        </div>
                  }
                    </div>
                }
                </div>
              }

              {/* VIDEOS TAB */}
              {activeTab === 'videos' &&
              <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-white text-lg font-bold">Match Highlights &amp; Videos</h2>
                    <span className="text-white/40 text-xs">{videoHighlights.length} Videos</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videoHighlights.map((video, i) =>
                  <VideoCard key={i} {...video} />
                  )}
                  </div>
                </div>
              }

              {/* POINTS TABLE TAB */}
              {activeTab === 'points-table' &&
              <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-white text-lg font-bold">Points Table</h2>
                    <span className="text-white/40 text-xs">Updated after Match {completedMatches.length}</span>
                  </div>
                  <div
                  className="rounded-xl overflow-hidden border border-rc-border"
                  style={{ backgroundColor: '#07090d' }}>
                  
                    <PointsTable data={tour.pointsTable} />
                  </div>
                </div>
              }

              {/* SQUADS TAB */}
              {activeTab === 'squads' &&
              <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-white text-lg font-bold">Team Squads</h2>
                    <span className="text-white/40 text-xs">{tour.teams?.length} Teams</span>
                  </div>
                  <SquadsTab squads={tour.squads} />
                </div>
              }

              {/* STATS TAB */}
              {activeTab === 'stats' &&
              <div>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-white text-lg font-bold">Tournament Statistics</h2>
                    <span className="text-white/40 text-xs">After {completedMatches.length} Matches</span>
                  </div>
                  <div
                  className="rounded-xl overflow-hidden border border-rc-border"
                  style={{ backgroundColor: '#07090d' }}>
                  
                    <StatsTab battingStats={tour.battingStats} bowlingStats={tour.bowlingStats} />
                  </div>
                </div>
              }
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-72 xl:w-80 flex-shrink-0 space-y-5">
              {/* Tournament Info Card */}
              <div
                className="rounded-xl border border-rc-border p-5"
                style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
                
                <h3 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Tournament Info</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <svg className="w-4 h-4 text-rc-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-white/40 text-xs">Duration</p>
                      <p className="text-white/85 text-sm">{tour.startDate} - {tour.endDate}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-4 h-4 text-rc-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-white/40 text-xs">Venue</p>
                      <p className="text-white/85 text-sm">{tour.venue}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-4 h-4 text-rc-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <div>
                      <p className="text-white/40 text-xs">Format</p>
                      <p className="text-white/85 text-sm">{tour.format}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-4 h-4 text-rc-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div>
                      <p className="text-white/40 text-xs">Total Matches</p>
                      <p className="text-white/85 text-sm">{tour.totalMatches} Matches</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-4 h-4 text-rc-orange mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-white/40 text-xs">Teams</p>
                      <p className="text-white/85 text-sm">{tour.teams?.length} Teams</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teams List */}
              <div
                className="rounded-xl border border-rc-border p-5"
                style={{ backgroundColor: 'rgb(18, 23, 33)' }}>
                
                <h3 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Participating Teams</h3>
                <div className="space-y-2.5">
                  {tour.teams?.map((team) =>
                  <div key={team.id} className="flex items-center gap-3">
                      {team.logo ?
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-rc-border flex-shrink-0">
                          <img src={team.logo} alt={`${team.name} cricket team logo`} className="w-full h-full object-cover" />
                        </div> :

                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                      style={{ backgroundColor: team.color + '33', border: `1px solid ${team.color}66` }}>
                      
                          {team.shortName.slice(0, 3)}
                        </div>
                    }
                      <div>
                        <p className="text-white/85 text-sm font-medium">{team.name}</p>
                        <p className="text-white/40 text-xs">{team.shortName}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Subscribe CTA */}
              <div
                className="rounded-xl overflow-hidden border border-rc-orange/30 p-5 relative"
                style={{ background: 'linear-gradient(135deg, #1a0800 0%, #07090d 100%)' }}>
                
                <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-rc-orange/5 -translate-y-8 translate-x-8" />
                <h3 className="text-white font-bold text-base mb-1">Watch Live</h3>
                <p className="text-white/50 text-xs mb-4">Get unlimited access to live matches, highlights &amp; more</p>
                <Link
                  href="/subscribe"
                  className="inline-flex items-center gap-2 w-full justify-center px-4 py-2.5 bg-rc-orange hover:bg-primary-dark text-white text-sm font-bold rounded-lg transition-all duration-200">
                  
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                  Subscribe Now
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 pb-4">
          <div className="flex items-center gap-2 text-xs">
            <Link href="/" className="text-white/40 hover:text-white transition-colors">Home</Link>
            <svg className="w-3 h-3 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link href="/cricket" className="text-white/40 hover:text-white transition-colors">Cricket</Link>
            <svg className="w-3 h-3 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white/60">{tour.name}</span>
          </div>
        </div>
      </main>
      <Footer />
    </div>);

}